<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { Trash, CheckCircle, XCircle } from 'phosphor-svelte';
  import { popup } from '@skeletonlabs/skeleton';
  import { CreditCardType, DirectFeedFolder } from '$lib/models/ccr/types';
  import type { FeedFilePending, FeedFileArchived } from '$lib/models/ccr/types';
  import { getFileErrorReasons, DELETE_FIRST_NOTE, type FileErrorDetail } from '$lib/utils/ccr/ccrFileErrorUtility';
  import Tooltip from '$lib/components/common/tooltip/Tooltip.svelte';
  import { GetHoverPopupSettings } from '$lib/components/common/tooltip/utils';

  type FileWithFolder = (FeedFilePending | FeedFileArchived) & {
    folder: DirectFeedFolder;
    status?: string;
  };

  export let files: FileWithFolder[] = [];
  export let creditCardType: CreditCardType | undefined = undefined;
  export let starterDataValidation: { filename: string; isValid: boolean; message?: string; missingDataWarnings?: string[] } | null = null;

  function hasCardDetails(file: FileWithFolder): boolean {
    return creditCardType === CreditCardType.Visa && 'hasCardDetails' in file && file.hasCardDetails === true;
  }

  const dispatch = createEventDispatcher();

  function formatDate(dateStr: string | undefined): string {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function getCompanyName(file: FeedFilePending | FeedFileArchived): string {
    if ('allCompanyNames' in file && file.allCompanyNames && file.allCompanyNames.length > 0) {
      return file.allCompanyNames.join(', ');
    }
    return '';
  }

  function getFileErrors(file: FileWithFolder): FileErrorDetail[] {
    const errors: FileErrorDetail[] = [];

    if (starterDataValidation && starterDataValidation.filename === file.name && !starterDataValidation.isValid) {
      if (starterDataValidation.missingDataWarnings && starterDataValidation.missingDataWarnings.length > 0) {
        errors.push(
          ...starterDataValidation.missingDataWarnings.map((warning) => ({
            reason: warning,
            fix: `${DELETE_FIRST_NOTE} Then have the provider re-export the first Visa file with full account setup records — account number and cardholder name — for every card, not just transactions, and upload it.`,
          })),
        );
      } else {
        errors.push({
          reason: starterDataValidation.message || 'Missing starter data',
          fix: `${DELETE_FIRST_NOTE} Then have the provider re-export the first Visa file with account setup records (account number and cardholder name) for every card before any transactions can be processed, and upload it.`,
        });
      }
    }

    errors.push(...getFileErrorReasons(file));

    return errors;
  }

  function getValidationStatus(errors: FileErrorDetail[], file: FileWithFolder): 'success' | 'error' | 'unknown' {
    if (errors.length > 0) {
      return 'error';
    }

    if (file.status === 'success') {
      return 'success';
    }

    if ('accountsFound' in file && file.accountsFound.length > 0) {
      return 'success';
    }

    return 'unknown';
  }

  function handleDelete(file: FileWithFolder) {
    dispatch('delete', { folder: file.folder, filename: file.name });
  }

  function shouldShowDeleteButton(validationStatus: 'success' | 'error' | 'unknown', file: FileWithFolder): boolean {
    // Show delete if: Correct Format is false OR isTestFile OR txt/csv file
    const isInvalidFormat = validationStatus === 'error';

    const isTestFile = 'isTestFile' in file && file.isTestFile === true;

    const fileType = file.fileType?.toLowerCase() || '';
    const isTextOrCsv = fileType === 'txt' || fileType === 'csv';

    return isInvalidFormat || isTestFile || isTextOrCsv;
  }

  // Computed once per file per render instead of re-running getFileErrors/getValidationStatus
  // 3-4x per row from the template, and gives each error row a stable, collision-free key.
  $: rows = files.map((file) => {
    const errors = getFileErrors(file);
    const validationStatus = getValidationStatus(errors, file);
    return {
      file,
      errors,
      validationStatus,
      showDeleteButton: shouldShowDeleteButton(validationStatus, file),
    };
  });
</script>

{#if files.length === 0}
  <div class="rounded-lg bg-gray-50 py-8 text-center text-gray-500">
    <p>No files</p>
  </div>
{:else}
  <div class="table-container h-auto overflow-x-auto rounded-2xl rounded-bl-none rounded-br-none border border-b-0 border-[--tableBorder]">
    <table class="table table-hover table-auto rounded-2xl rounded-bl-none rounded-br-none lg:table-fixed">
      <thead class="!bg-[--tableHeaderBackground]">
        <tr>
          <th class="!text-md h-[39px] w-[26%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">File Name</th>
          <th class="!text-md h-[39px] w-[10%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">File Type</th>
          <th class="!text-md h-[39px] w-[15%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Company Name</th>
          <th class="!text-md h-[39px] w-[15%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Uploaded At</th>
          <th class="!text-md h-[39px] w-[10%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Test File</th>
          <th class="!text-md h-[39px] w-[14%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Correct Format</th>
          <th class="!text-md h-[39px] w-[10%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Actions</th>
        </tr>
      </thead>
      <tbody>
        {#each rows as { file, errors, validationStatus, showDeleteButton } (file.path)}
          <tr class="!border-[--tableBorder]">
            <td class="h-10 w-[26%] content-center !px-5">
              <div class="text-md flex items-center gap-1.5 truncate font-normal text-primary-500" title={file.name}>
                {#if hasCardDetails(file)}
                  <CheckCircle size={14} weight="fill" class="shrink-0 text-green-600" aria-label="Has card details" />
                {/if}
                <span class="truncate">{file.name}</span>
              </div>
            </td>
            <td class="h-10 w-[10%] content-center !px-5">
              <div class="text-md flex items-center font-normal uppercase text-primary-500">
                {file.fileType || 'pgp'}
              </div>
            </td>
            <td class="h-10 w-[15%] content-center !px-5">
              <div class="text-md flex items-center font-normal text-primary-500">
                {getCompanyName(file)}
              </div>
            </td>
            <td class="h-10 w-[15%] content-center !px-5">
              <div class="text-md flex items-center font-normal text-primary-500">
                {formatDate(file.createdAt)}
              </div>
            </td>
            <td class="h-10 w-[10%] content-center !px-5">
              {#if 'isTestFile' in file && file.isTestFile}
                <span class="text-primary-500">Yes</span>
              {:else}
                <span class="text-primary-500">No</span>
              {/if}
            </td>
            <td class="h-10 w-[14%] content-center !px-5">
              {#if validationStatus === 'success'}
                <div class="flex items-center gap-2 text-green-600">
                  <CheckCircle size={16} weight="fill" />
                  <span>Success</span>
                </div>
              {:else if validationStatus === 'error'}
                <div class="flex flex-col gap-1">
                  {#each errors as fileError, errorIndex (errorIndex)}
                    <div class="flex items-center gap-1" use:popup={GetHoverPopupSettings(`ccr-file-error_${file.path}_${errorIndex}`)}>
                      <XCircle size={14} weight="fill" class="shrink-0 text-red-600" />
                      <span class="truncate text-xs text-red-600">{fileError.reason}</span>
                      <Tooltip config={GetHoverPopupSettings(`ccr-file-error_${file.path}_${errorIndex}`)}>
                        <p class="font-medium">{fileError.reason}</p>
                        <p class="mt-1">{fileError.fix}</p>
                      </Tooltip>
                    </div>
                  {/each}
                </div>
              {:else}
                <span class="text-gray-400">-</span>
              {/if}
            </td>
            <td class="h-10 w-[10%] content-center !px-5">
              <div class="flex items-center justify-center">
                {#if showDeleteButton}
                  <button class="rounded p-1 text-red-600 transition-colors hover:bg-red-50" on:click={() => handleDelete(file)} title="Delete file">
                    <Trash size={16} />
                  </button>
                {/if}
              </div>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}
